import Model, { attr } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';

type Highlight = {
  [key: string]: string[];
};

// Index statistics response
export interface IndexStats {
  index: string;
  docCount: number;
  size: string;
  sizeInBytes: number;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  rawStats: {
    health: Record<string, unknown>;
    stats: Record<string, unknown>;
  };
}

type Employee = {
  id: number;
  personId: number;
  companyId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  payrollId: string;
  email: string;
  workspaceId: number;
  tasParticipantState: string;
  participationStatus: string;
  hasAccounts: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type TasProgramInstance = {
  id: string;
  companyId: string;
  companyName: string;
  employeeId: number;
  employeeIdKeyword?: string;
  employeeNameKeyword: string;
  employeePayrollIdKeyword: string;
  personId: number;
  employmentEndDate: string | null;
  employmentTerminationVerified: boolean;
  tasProgramInstanceAttentionPriorityDate?: string;
  tasProgramInstanceIdKeyword?: string;
  tasProgramInstanceCreatedAtDate?: string;
  tasProgramInstanceStateKeyword?: string;
  tasProgramTemplateIdKeyword?: string;
  tasProgramTemplateTypeKeyword?: string;
  tasProgramInstanceLatestSubmissionTs?: string;
  tasProgramInstanceLatestStateChangeAtDate?: string;
  tasProgramInstanceLatestStateChangePersonText?: string;
  tasApplicationLatestSubmissionTs?: string;

  // NEW STANDARDIZED PROGRAM INSTANCE FIELDS (Phase 3 - from TasField with _field_ indicator)
  tasProgramInstanceFieldEmployeeIdInteger?: number;
  tasProgramInstanceFieldEmployeeIdKeyword?: string;
  tasProgramInstanceFieldEmployeeEmailKeyword?: string;
  tasProgramInstanceFieldEstimatedProgramBeginDate?: string;
  tasProgramInstanceFieldEstimatedProgramCompletionDate?: string;
  tasProgramInstanceFieldFtPtOtherKeyword?: string;
  tasProgramInstanceFieldNameKeyword?: string;
  tasProgramInstanceFieldProgramSpecializationKeyword?: string;
  tasProgramInstanceFieldSchoolInstitutionNameKeyword?: string;
  tasProgramInstanceFieldEmployeeTitleKeyword?: string;
  tasProgramInstanceFieldDapipInstitutionIdInteger?: number;
  tasProgramInstanceFieldHireDateDate?: string;

  // Program template fields with keyword variants
  tasProgramTemplateFieldProgramNameKeyword?: string;
  tasProgramTemplateFieldProgramSpecializationKeyword?: string | null;
  tasProgramTemplateFieldVoucherProgramBoolean?: boolean;

  // NEW STANDARDIZED APPLICATION FIELDS (Phase 2)
  recordType?: string; // 'application' or 'instance'
  tasApplicationIdKeyword?: string;
  tasApplicationStateKeyword?: string;
  tasApplicationCreatedAtDate?: string;
  tasApplicationAttentionPriorityDate?: string;
  tasApplicationLatestStateChangePersonText?: string;
  tasApplicationLatestStateChangeAtDate?: string;
  tasApplicationCurrentReviewersKeyword?: number[];
  tasApplicationPossibleReviewersKeyword?: number[];
  tasApplicationCourseCashReservedAmountInteger?: number;
  tasApplicationCourseCashUsedAmountInteger?: number;

  // NEW STANDARDIZED APPROVAL/WORKFLOW FIELDS (Phase 4 - calculated fields)
  programApprovalRequired?: boolean;
  courseApprovalRequired?: boolean;
  courseGradeRequired?: boolean;
  evidenceApprovalRequired?: boolean;
  approverCanEditApplication?: boolean;
  applicationInvoiceDate?: string | null;

  // Program instance approval tracking
  programInstanceAffirmedReviewers?: unknown[];
  programInstanceCurrentReviewers?: number[];
  programInstancePossibleReviewers?: number[];
  tasProgramInstanceApprovalCountSubmissions?: number | null;
  tasProgramInstanceApprovalFirstSubmissionTs?: string | null;
  tasProgramInstanceApprovalCurrentApprovalRole?: string | null;
  tasProgramInstanceCompletionCountSubmissions?: number | null;
  tasProgramInstanceCompletionFirstSubmissionTs?: string | null;
  tasProgramInstanceCompletionCurrentApprovalRole?: string | null;
  tasProgramInstanceWithdrawalCountSubmissions?: number | null;
  tasProgramInstanceWithdrawalFirstSubmissionTs?: string | null;
  tasProgramInstanceWithdrawalCurrentApprovalRole?: string | null;

  // Application approval tracking
  tasApplicationCoursesCountSubmissions?: number | null;
  tasApplicationCoursesFirstSubmissionTs?: string | null;
  tasApplicationCoursesCurrentApprovalRole?: string | null;
  tasApplicationEvidenceCountSubmissions?: number | null;
  tasApplicationEvidenceFirstSubmissionTs?: string | null;
  tasApplicationEvidenceCurrentApprovalRole?: string | null;
  tasApplicationFieldCoursesEndDateDate?: string;
  tasApplicationFieldCoursesBeginDateDate?: string;
  tasApplicationFieldApplicationApproveCoursesWorkflowKeyword?: string;
  tasApplicationFieldApplicationApproveEvidenceWorkflowKeyword?: string;

  // Phase 7: Flattened custom fields for each fieldable type
  // Using OpenSearch flattened datatype to prevent mapping explosion
  tasProgramTemplateCustomFields?: Record<string, unknown>;
  tasProgramInstanceCustomFields?: Record<string, unknown>;
  tasApplicationCustomFields?: Record<string, unknown>;
  tasCourseCustomFields?: Record<string, unknown>;

  // Withdrawal approval tracking
  withdrawalApproval?: {
    affirmed: unknown[];
    approved: boolean;
    required: unknown[];
    currentReviewers: unknown[];
    possibleReviewers: unknown[];
  };

  // Payment tracking fields
  paymentLastDate?: string | null;
  paymentPreApprovedTotal?: number;
  paymentFinalApprovedTotalAmount?: number;
  paymentPendingAmount?: number;
  paymentPaidAmount?: number;
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
  paymentRefundLimitAmount?: number;

  // NEW STANDARDIZED COURSE FIELDS (Phase 6 - from TasField with _field_ indicator)
  courses?: {
    courseId: string;
    tasCourseFieldCourseNameKeyword?: string;
    tasCourseFieldCourseNumberKeyword?: string;
    tasCourseFieldCourseNumberInteger?: number;
    tasCourseFieldCourseDescriptionKeyword?: string;
    tasCourseFieldCourseTuitionInteger?: number;
    tasCourseFieldCourseBooksInteger?: number;
    tasCourseFieldCourseFeesInteger?: number;
    tasCourseFieldCourseGrade?: string;
    tasCourseFieldCourseCreditFloat?: number;
    tasCourseFieldApprovedCourseTotalInteger?: number;
    tasCourseCustomFields?: Record<string, unknown>;
  }[];

  // OpenSearch metadata
  updatedAt?: string;
  indexedAt?: string;
  documentCreatedAt?: string;

  // TasAsset document fields (record_type === 'asset')
  tasAssetIdKeyword?: string;
  tasAssetFilenameKeyword?: string;
  tasAssetNameKeyword?: string;
  tasAssetClassificationKeyword?: string;
  tasAssetDescriptionKeyword?: string;
  tasAssetCreatedAtDate?: string;
  tasAssetUpdatedAtDate?: string;
  attentionPriorityDate?: string;

  // Root-level approvable reviewer fields (TasAsset documents)
  tasApprovableCurrentReviewersKeyword?: number[];
  tasApprovablePossibleReviewersKeyword?: number[];

  // Approval queue state fields (TasAsset documents)
  tasAssetHasPendingApprovablesBoolean?: boolean;
  tasApprovableStatesKeyword?: string[];
};

export interface ApplicationStateCount {
  key: string;
  count: number;
}

export interface AggregatedProgramInstance {
  employeeId: string;
  employeeNameKeyword: string;
  employeePayrollIdKeyword: string;
  programInstanceId: string;
  tasProgramInstanceIdKeyword: string; // Canonical field name for column mapping
  tasProgramInstanceCreatedAtDate: string;
  tasProgramTemplateFieldProgramNameKeyword: string; // ✅ CORRECTED: This field contains program template name (e.g., "MBA", "Bachelor's Degree")
  tasProgramInstanceFieldNameKeyword?: string; // DEPRECATED: Alias for backward compatibility with old column selections
  tasProgramInstanceFieldSchoolInstitutionNameKeyword: string;
  applicationStateCounts: Record<string, number>;
  totalApplications: number;
}

export type Person = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;

  employees: Employee[];
  isActive: boolean;
  isEnrolled: boolean;
  /** Person-level active flag (person_is_active_boolean). Used for the User-active filter/chip. */
  personIsActiveBoolean?: boolean;
  userNames: string[];

  jurisdictions: string[];
  highlight: Highlight;
  /** Names of the query clauses this hit matched (from OpenSearch `matched_queries`). */
  matchedQueries?: string[];
};

export type PslfForm = {
  borrowerEmail: string;
  borrowerName: string;
  borrowerSignDate: string;
  companyId: string;
  employerClosed: string;
  employerEmail: string;
  employerName: string;
  employerSignDate: string;
  formAwaitingSignature: boolean;
  formFaxVerified: boolean;
  formId: string;
  formPreApproved: boolean;
  formPrintAndMail: boolean;
  formStatus: string;
  formNonPartner: boolean;
  formType: string;
  payrollId: string;
  profileId: string;
  pslfFormIsSubmittedOboBoolean?: boolean;
  pslfFormSubmittedOboByEmployeeIdKeyword?: string;
  createdAt: string;
  updatedAt: string;
};

export type TasUnifiedInstitution = {
  identifier: string;
  dapipId: number;
  institutionId: string | null;
  locationName: string;
  locationType: string;
  address: string;
  instIsFeatured: boolean;
  instIsPreferred: boolean;
  lpn: boolean;
};

export type CompanySearchResult = {
  companyIdKeyword: string;
  companyNameText: string;
  companyNameKeyword: string;
  companyTypeKeyword: string;
  companyIsActiveBoolean: boolean;
  companySubDomainKeyword: string;
  companyProductsEnabledKeyword: string[];
  companyProgramTitleText?: string;
  companyIsTaEnabledBoolean?: boolean;
  companyIsTaOnlyBoolean?: boolean;
  companyIsLiveBoolean?: boolean;
  companyHasSsoBoolean?: boolean;
  companyLaunchDateDate?: string;
  companyCreatedAtDate?: string;
  companyPartnerTypeKeyword?: string;
  companyFeaturesKeyword?: string[];
  companyAdminPersonNameText?: string;
  companyAdminUserEmailsText?: string[];
  companyPartnerSuccessAccountManagerNameText?: string;
};

// ── Approvable index document ──────────────────────────────────────────
// Represents a single document from the unified approvable OpenSearch index.
// The index is polymorphic — shared by TasApprovable, PslfApprovable,
// SyfApprovable, and TasEligibilityApprovable. Fields are product-gated:
// TAS fields are only populated for TAS approvables, PSLF for PSLF, etc.
export type ApprovableProduct = 'TAS' | 'PSLF' | 'SYF' | 'SLR' | 'PCS';
export type ApprovableState =
  | 'ApprovableState.PREPARING'
  | 'ApprovableState.PENDING'
  | 'ApprovableState.APPROVED'
  | 'ApprovableState.REJECTED'
  | 'ApprovableState.ABANDONED'
  | 'ApprovableState.ERRORED';
export type ApprovableWorkflow =
  | 'TAS_APPROVAL'
  | 'PSLF_FORM'
  | 'SYF_MATCH'
  | 'SLR_CONTRIBUTION'
  | 'PCS_CARD_REVIEW';

export type ApprovableDocument = {
  // Core identity & classification
  approvableRecordIdKeyword: string;
  approvableIdKeyword?: string;
  approvableTypeKeyword?: string;
  approvableClassKeyword?: string;
  approvalKeyword?: string;
  stateKeyword?: ApprovableState;
  contextStateKeyword?: string;
  productKeyword?: ApprovableProduct;
  approvableWorkflowKeyword?: ApprovableWorkflow;
  companyIdKeyword?: string;
  personIdKeyword?: string;
  contextTypeKeyword?: string;
  contextIdKeyword?: string;

  // Cross-product unified fields
  programIdKeyword?: string;
  programNameKeyword?: string;
  programCodeKeyword?: string;
  groupIdKeyword?: string;
  groupNameKeyword?: string;
  enrollmentIdKeyword?: string;
  coverageStartDate?: string;
  coverageEndDate?: string;

  // Lifecycle & submission
  approvableCreatedAtDate?: string;
  approvableUpdatedAtDate?: string;
  approvableSubmittedForReviewAtDate?: string;
  approvableSubmittedByPersonIdKeyword?: string;
  approvableSubmittedByNameKeyword?: string;
  approvableSubmittedByRoleKeyword?: string;
  approvableSubmittedByRelationshipKeyword?: string;
  approvableInReviewSinceDate?: string;
  attentionPriorityDate?: string;

  // Progress & metrics
  approvableCurrentOrdinalInteger?: number;
  approvableMaxOrdinalInteger?: number;
  approvableAffirmedCountInteger?: number;
  approvablePossibleReviewerCountInteger?: number;
  approvableCurrentReviewerCountInteger?: number;
  approvableRemainingReviewerCountInteger?: number;
  approvableProgressPercentDouble?: number;
  approvableHoursSinceSubmittedDouble?: number;
  approvableHoursInReviewDouble?: number;
  approvableHoursWaitingOnCurrentReviewerDouble?: number;
  approvableReviewCycleCountInteger?: number;
  approvableDaysInCurrentStateInteger?: number;
  approvableIsActionableBoolean?: boolean;

  // Evidence completeness
  evidenceRequiredBoolean?: boolean;
  evidenceSubmittedBoolean?: boolean;
  evidenceCompleteBoolean?: boolean;
  evidenceExpectedCountInteger?: number;
  evidenceSubmittedCountInteger?: number;
  evidenceMissingTypesKeyword?: string[];

  // Approval step metadata (TAS)
  tasStepNameKeyword?: string;
  tasStepDescriptionKeyword?: string;
  tasOrdinalNamesKeyword?: string[];

  // Reviewers (current & possible)
  currentReviewerEmployeeIdsKeyword?: string[];
  currentReviewerPersonIdsKeyword?: string[];
  currentReviewerDotsKeyword?: string[];
  currentReviewerRelationshipsKeyword?: string[];
  currentReviewerFullNamesKeyword?: string[];
  possibleReviewerEmployeeIdsKeyword?: string[];
  possibleReviewerPersonIdsKeyword?: string[];
  possibleReviewerDotsKeyword?: string[];
  possibleReviewerRelationshipsKeyword?: string[];
  possibleReviewerFullNamesKeyword?: string[];

  // "Waiting for" reviewer subset
  approvableWaitingForCountInteger?: number;
  approvableWaitingForReviewerEmployeeIdsKeyword?: string[];
  approvableWaitingForReviewerPersonIdsKeyword?: string[];
  approvableWaitingForReviewerDotsKeyword?: string[];
  approvableWaitingForReviewerRelationshipsKeyword?: string[];
  approvableWaitingForReviewerFullNamesKeyword?: string[];

  // Employee & company
  employeeIdKeyword?: string;
  employeeNameKeyword?: string;
  employeePayrollIdKeyword?: string;
  employeeIsActiveBoolean?: boolean;
  employeeEmploymentTypeKeyword?: string;
  employeeDepartmentKeyword?: string;
  companyNameKeyword?: string;

  // Digital asset
  approvableAssetIdKeyword?: string;
  approvableAssetUuidKeyword?: string;
  approvableAssetFilenameKeyword?: string;
  approvableAssetNameKeyword?: string;
  approvableAssetClassificationKeyword?: string;
  approvableAssetContentTypeKeyword?: string;
  approvableAssetDescriptionKeyword?: string;
  approvableAssetTagsKeyword?: string[];

  // Attachment
  attachmentIdKeyword?: string;
  attachmentClassificationKeyword?: string;
  attachmentNameKeyword?: string;

  // Comments & tags
  approvableCommentAuthorsKeyword?: string[];
  approvableCommentAuthorPersonIdsKeyword?: string[];
  approvableCommentTagsKeyword?: string[];
  contextTagsKeyword?: string[];

  // ── TAS-specific fields ─────────────────────────────────────────────
  tasAssistanceIdKeyword?: string;
  tasAssistanceStateKeyword?: string;
  tasAssistanceAmountInteger?: number;
  tasAssistanceMannerKeyword?: string;
  tasApplicationIdKeyword?: string;
  tasApplicationStateKeyword?: string;
  tasApplicationInLimitLedgerNegationStateBoolean?: boolean;
  tasApplicationCoursesBeginDate?: string;
  tasApplicationCoursesEndDate?: string;
  tasApplicationEvidenceDeadlineDate?: string;
  tasRequestedAmountCentsInteger?: number;
  tasApprovedAmountCentsInteger?: number;
  tasPaidAmountCentsInteger?: number;
  tasCourseCountInteger?: number;
  tasProgramInstanceIdKeyword?: string;
  tasProgramInstanceStateKeyword?: string;
  tasProgramTemplateIdKeyword?: string;
  tasProgramTemplateCodeKeyword?: string;
  tasProgramTemplateNameKeyword?: string;
  tasProgramTemplateExpectedDocumentTypesKeyword?: string[];
  tasGroupIdKeyword?: string;
  tasGroupNameKeyword?: string;
  tasProgramSpecializationKeyword?: string;
  tasCourseIdKeyword?: string;
  tasCourseNameKeyword?: string;
  tasParticipantIdKeyword?: string;
  tasParticipantStateKeyword?: string;
  tasParticipantPayrollIdKeyword?: string;

  // ── PSLF-specific fields ────────────────────────────────────────────
  pslfFormIdKeyword?: string;
  pslfFormStateKeyword?: string;
  pslfFormTypeKeyword?: string;
  pslfDocumentIdKeyword?: string;
  pslfGroupIdKeyword?: string;
  pslfTemplateIdKeyword?: string;
  pslfTemplateNameKeyword?: string;
  pslfGroupNameKeyword?: string;
  pslfFaxVerifiedKeyword?: string;
  pslfFaxIdKeyword?: string;
  pslfApproverEmployeeIdKeyword?: string;
  pslfApproverPersonIdKeyword?: string;
  pslfApproverNameKeyword?: string;
  pslfApproverEmailKeyword?: string;
  pslfIsNonPartnerKeyword?: string;
  pslfIsPreApprovedKeyword?: string;
  pslfBorrowerCompletedAtDate?: string;
  pslfEmployerCompletedAtDate?: string;
  pslfSubmittedAtDate?: string;
  pslfBorrowerNameKeyword?: string;
  pslfEmployerNameKeyword?: string;
  pslfEmployerEinKeyword?: string;
  // Employment verification
  pslfEmploymentStartDate?: string;
  pslfEmploymentEndDate?: string;
  pslfEmploymentContinuesBoolean?: boolean;
  pslfEmploymentHoursFloat?: number;
  pslfEmploymentTypeKeyword?: string;
  pslfEmploymentTenureMonthsInteger?: number;
  pslfEmployerQualifiesBoolean?: boolean;
  pslfEmployerIsGovernmentBoolean?: boolean;
  pslfEmployerIsNotForProfitBoolean?: boolean;
  // Signature & signing state
  pslfBorrowerSignDate?: string;
  pslfEmployerSignDate?: string;
  pslfAwaitingSignatureBoolean?: boolean;
  pslfRequiresEmployerSignatureBoolean?: boolean;
  pslfAutoSignEnabledBoolean?: boolean;
  pslfPriorEmployerClosedBoolean?: boolean;
  pslfHasApproverProfileBoolean?: boolean;
  pslfInApproverQueueBoolean?: boolean;
  // Forgiveness progress
  pslfQualifyingPaymentsInteger?: number;
  pslfRemainingPaymentsInteger?: number;
  pslfProgressPercentFloat?: number;
  pslfForgivenessReachedBoolean?: boolean;
  pslfCurrentMilestoneKeyword?: string;
  pslfHasLinkedLoansBoolean?: boolean;
  pslfLinkedLoanCountInteger?: number;
  pslfPaymentDataAvailableBoolean?: boolean;
  // Compliance flags
  pslfHasIdrPlanBoolean?: boolean;
  pslfQualificationRiskBoolean?: boolean;
  pslfCertificationOverdueBoolean?: boolean;
  // Field conflicts
  pslfHasFieldConflictBoolean?: boolean;
  pslfFieldConflictCountInteger?: number;
  // Form context
  pslfIsCurrentEmployerBoolean?: boolean;
  pslfIsPriorEmployerBoolean?: boolean;
  pslfBorrowerReasonForgivenessBoolean?: boolean;
  pslfBorrowerReasonForbearanceBoolean?: boolean;
  pslfPrintAndMailBoolean?: boolean;

  // ── SYF/SLR-specific fields ─────────────────────────────────────────
  syfMatchParticipantIdKeyword?: string;
  syfMatchParticipantStateKeyword?: string;
  syfMatchParticipantEnrollmentStateKeyword?: string;
  syfMatchParticipantPayrollIdKeyword?: string;
  syfMatchParticipantPlanIdKeyword?: string;
  syfMatchPlanNameKeyword?: string;
  syfMatchPlanCodeKeyword?: string;
  syfBenefitStartDateDate?: string;
  syfBenefitEndsAfterDate?: string;
  syfPaymentsQualifiedAsOfDate?: string;

  // Transaction fields (SYF/SLR via Attachment join)
  approvableTransactionIdsKeyword?: string[];
  approvableTransactionVerificationStatesKeyword?: string[];
  approvableTransactionPendingVerificationCountInteger?: number;
  approvableTransactionPendingVerificationBoolean?: boolean;
  approvableTransactionSlrPaymentCountInteger?: number;
  approvableTransactionSlrPaymentBoolean?: boolean;
  approvableTransactionAccountingLineItemStatesKeyword?: string[];
  approvableTransactionPendingCreditCountInteger?: number;
  approvableTransactionPendingCreditBoolean?: boolean;

  // ── PCS-specific fields (Course Cash) ───────────────────────────────
  pcsCardIdKeyword?: string;
  pcsCardStateKeyword?: string;
  pcsCardProgramKeyword?: string;
  pcsCardHolderNameKeyword?: string;
  pcsAuthorizationIdKeyword?: string;
  pcsAuthorizationAmountInteger?: number;
  pcsAuthorizationStateKeyword?: string;
  pcsTransactionIdKeyword?: string;
  pcsTransactionAmountInteger?: number;
  pcsTransactionStateKeyword?: string;
  pcsMerchantNameKeyword?: string;
  pcsMerchantNetworkIdKeyword?: string;
  pcsMerchantMccKeyword?: string;
  pcsMerchantCityKeyword?: string;
  pcsMerchantStateKeyword?: string;
  pcsTotalAuthorizedAmountInteger?: number;
  pcsTotalCapturedAmountInteger?: number;
  pcsAuthorizationCountInteger?: number;
  pcsTransactionCountInteger?: number;
  pcsHasDeclinedBoolean?: boolean;
  pcsHasRefundedBoolean?: boolean;
  pcsLatestEventDate?: string;

  // OpenSearch metadata
  updatedAt?: string;
  indexedAt?: string;
};

// ── Approvable counts/aggregation response ────────────────────────────
// Response from POST /api/v2/search/approvables/counts
export interface ApprovableCountsBucket {
  key: string;
  docCount: number;
}

export interface ApprovableCountsAggregation {
  docCountErrorUpperBound: number;
  sumOtherDocCount: number;
  buckets: ApprovableCountsBucket[];
}

export interface ApprovableFilterCount {
  docCount: number;
}

export interface ApprovableCountsResponse {
  byProduct?: ApprovableCountsAggregation;
  byState?: ApprovableCountsAggregation;
  byWorkflow?: ApprovableCountsAggregation;
  byCompany?: ApprovableCountsAggregation;
  byApprovalType?: ApprovableCountsAggregation;
  byContextType?: ApprovableCountsAggregation;
  myTurnCount?: ApprovableFilterCount;
  waitingOnOthersCount?: ApprovableFilterCount;
  total: number;
}

// Typed OpenSearch response for approvable queries
export type ApprovableSearchResponse = OpenSearchResponse<ApprovableDocument>;

export type ResultItems =
  | Person
  | PslfForm
  | TasProgramInstance
  | TasUnifiedInstitution
  | CompanySearchResult
  | ApprovableDocument;

// Raw OpenSearch response structure
export interface OpenSearchHit<T = unknown> {
  _index: string;
  _id: string;
  _score: number;
  _routing?: string;
  _source: T;
  highlight?: Record<string, string[]>;
  /** Names of matched named queries. Camelized by the API; raw form is `matched_queries`. */
  matchedQueries?: string[];
  matched_queries?: string[];
}

export interface OpenSearchHits<T = unknown> {
  total: {
    value: number;
    relation: string;
  };
  maxScore: number;
  hits: OpenSearchHit<T>[];
}

// Aggregation bucket structures
export interface ApplicationStateBucket {
  key: string;
  docCount: number;
}

export interface SampleDocumentHits<T = unknown> {
  total: {
    value: number;
    relation: string;
  };
  maxScore: number;
  hits: OpenSearchHit<T>[];
}

export interface EmployeeBucket {
  key: string;
  docCount: number;
  applicationStates: {
    docCountErrorUpperBound: number;
    sumOtherDocCount: number;
    buckets: ApplicationStateBucket[];
  };
  sampleDocument: {
    hits: SampleDocumentHits<TasProgramInstance>;
  };
}

export interface EmployeesAggregation {
  docCountErrorUpperBound: number;
  sumOtherDocCount: number;
  buckets: EmployeeBucket[];
}

/**
 * Field metadata injected by the API into search responses.
 *
 * `resolvedSource` is the canonical, expanded list of `_source` paths that
 * the backend recognized for the request. It is present only when the
 * request included a `_source` filter. Wildcards are expanded against the
 * known field set, and `_keyword`/dotted-container aliases are coerced to
 * their canonical paths. Keys the backend could not resolve are dropped
 * (and logged server-side via `search.field_meta.unresolved_requested_source`).
 *
 * Callers can use this to reconcile their selected-column state and prune
 * keys that would otherwise return blank values from OpenSearch.
 */
export interface SearchFieldMeta {
  resolvedSource?: string[];
  // Other field_meta keys (fields, aliases, etc.) are intentionally untyped
  // here; consumers that need them should narrow at the call site.
  [key: string]: unknown;
}

export interface OpenSearchResponse<T = unknown> {
  hits: OpenSearchHits<T>;
  aggregations?: {
    employees?: EmployeesAggregation;
  };
  took: number;
  timedOut?: boolean;
  _shards?: Record<string, unknown>;
  fieldMeta?: SearchFieldMeta;
}

export default class SearchModel extends Model {
  @attr declare items: unknown[];
  @attr declare total: number;
  @attr declare took: number;

  [Type] = 'search' as const;
}

export interface SearchModelWith<T> {
  items: T[];
  total: number;
  took: number;
  isLoading?: boolean;
}

// Type for raw OpenSearch responses
export type RawSearchResponse<T> = OpenSearchResponse<T>;

// Union type for results that can be either individual or aggregated
export type ProgramsSearchResult =
  | SearchModelWith<TasProgramInstance> // Individual mode
  | SearchModelWith<AggregatedProgramInstance> // Aggregated mode
  | SearchModelWith<Record<string, unknown>>; // Generic dataset mode

export interface SearchFieldCapabilities {
  fullText?: boolean;
  filter?: boolean;
  sort?: boolean;
  aggregate?: boolean;
  range?: boolean;
  nested?: boolean;
  object?: boolean;
}

export interface SearchFieldUiHints {
  filterWidget?: string;
  multiSelect?: boolean;
}

export interface SearchFieldPayload {
  name: string;
  label?: string;
  type?: string;
  sourceField?: string;
  capabilities?: SearchFieldCapabilities;
  uiHints?: SearchFieldUiHints;
  semanticRole?: string;
  semanticRoleLabel?: string;
  reportDatatype?: string;
  displayLabel?: string;
  isCustom?: boolean;
  enumValues?: string[];
  provenance?: string;
  // Rich fields from /api/v2/search/fields (ReportFieldsBuilder)
  group?: string;
  province?: string | null;
  visibility?: string;
  isDisplayable?: boolean;
  displayType?: string;
  path?: string;
  isFilter?: boolean;
  isSortable?: boolean;
  isAggregatable?: boolean;
  templateLabels?: Record<string, { label?: string; province?: string }>;
}

export interface EmployeeCustomFieldMapping {
  fieldName?: string;
  label?: string;
  sourceField?: string;
  reportDatatype?: string;
  enumValues?: string[];
}

export interface SearchFieldsResponse {
  fields?: SearchFieldPayload[];
  keyLabelMaps?: KeyLabelMapsResponse;
  employeeCustomFieldMappings?: EmployeeCustomFieldMappingPayload[];
}

export interface EmployeeCustomFieldMappingPayload {
  fieldName?: string;
  label?: string;
  sourceField?: string;
  reportDatatype?: string;
  enumValues?: string[];
  enumDot?: string;
  mappingType?: string;
  isCustom?: boolean;
}

export interface KeyLabelMapsResponse {
  companyEfKeyLabelMap?: Record<string, string>;
  tasTemplateKeyLabelMap?: Record<string, string>;
  tasTemplateKeyProvinceMap?: Record<string, string>;
  allTemplateLabels?: Record<string, Record<string, { label?: string; province?: string }>>;
}

export interface OpenSearchMapping {
  [indexName: string]: {
    mappings: {
      properties: {
        [fieldName: string]: {
          type: string;
          fields?: Record<string, { type: string }>;
          properties?: Record<string, unknown>;
        };
      };
    };
  };
}

export interface SearchMappingWithLabelMapsResponse {
  mapping?: OpenSearchMapping;
  labelMaps?: KeyLabelMapsResponse;
}

export interface ReportExportResponse {
  signed_url: string;
  filename: string;
  row_count: number;
  asset_id?: string;
}

/** Response from POST /search/export-asset (camelized by the API). */
export interface ExportAssetResponse {
  filename: string;
  rowCount: number;
  truncated?: boolean;
  asset: {
    id: number;
    uuid: string;
    bucket: string;
    fullPath: string;
    signedUrl: string;
  };
}

/** Options for POST /search/export-asset — the policied search-pipeline export. */
export interface ExportAssetOptions {
  modelType: string;
  /** The exact OpenSearch body the preview ran — "one body, two sinks". */
  searchBody: Record<string, unknown>;
  reportId?: string;
  companyId?: string;
  columnKeys?: string[];
  columnLabels?: Record<string, string>;
  columnFormats?: Record<string, unknown>;
}

export interface SearchAdapter {
  search<T = unknown>(query: Record<string, unknown>): Promise<OpenSearchResponse<T>>;
  downloadCsv(query: Record<string, unknown>): Promise<void>;
  exportReport?(
    reportId: string,
    options?: {
      companyId?: string;
      searchBody?: Record<string, unknown>;
      modelType?: string;
      columnKeys?: string[];
      columnLabels?: Record<string, string>;
      columnFormats?: Record<string, unknown>;
    }
  ): Promise<ReportExportResponse>;
  exportAsset?(options: ExportAssetOptions): Promise<ExportAssetResponse>;
  fields?(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): Promise<SearchFieldsResponse>;
  mappingWithLabelMaps?(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): Promise<SearchMappingWithLabelMapsResponse>;
  mapping?(modelType: string): Promise<OpenSearchMapping>;
}
